// Serve Receive By Zones report: a court view of attacks originating from
// serve-receives (ball paths coloured by result), plus a breakdown table of
// serve-receive counts/attack efficiency by reception grade and by the
// LEFT/CENTRE/RIGHT channel the RECEPTION itself landed in (i.e. where it
// was passed to, not where the serve landed).
//
// Collation mirrors DVMate's SetterAnalysisView.m/SetterAnalysisStats
// exactly (see /Users/chaule/Projects/DVMate/SetterAnalysisView.m and
// .../Reports/SetterAnalysisFilterViewController.m):
//   - channel ("side") = the reception's own end zone, which is its
//     AdvanceCode4 (the raw scout-code zone digit at string position 10) —
//     matching DVMate's Event+sup.m endZoneNumber property exactly. NOT
//     BallEndString: DVWFile.js's createEvent only computes real ball-path
//     coordinates (bs/be) for serve/attack scout codes, so a reception's
//     BallEndString is always empty and zoneFromString(...) on it is always
//     0 — that was the original bug making every cell 0. Grouped zones
//     4/5/7 = LEFT, 3/6/8 = CENTRE, 1/2/9 = RIGHT (DVMate's side0/1/2
//     groupings).
//   - kills/errors come from the attack(s) belonging to whichever "Stages"
//     checkboxes (above the table, next to Reset) are checked: SIDE OUT is
//     the single attack immediately following the reception (DVMate's
//     SetterCallEvent.spikeEvent), TRANSITION is every later attack in the
//     same rally (after a dig) — matches DVMate's default (SIDE OUT only)
//     when nothing else is toggled
//   - efficiency = (kills - errors) * 100 / attacks, where `attacks` is the
//     number of counted attacks in that cell (NOT the reception count) —
//     this can go negative, same as DVMate
//   - Setters/Passers/Calls/Rotations/Sets filters (bottom-right panel)
//     restrict which receptions are counted at all: Setters/Calls are read
//     off the rally's own set/setter's-call event (same side as the
//     reception); a rally whose setter/call can't be determined always
//     passes through un-filtered (same "don't let missing data hide valid
//     rows" policy used for channel/grade elsewhere in DVMate)
//   - "SELECTED" row per channel = a read-only, live sum of whichever grade
//     cells in that column are currently toggled on (so it's never literally
//     every reception regardless of grade — receptions graded "=" or "/"
//     never have a cell to begin with). It isn't itself selectable, and its
//     own total never feeds the hitting chart below (the chart already sums
//     the exact same selected grade cells directly).
//   - frequency % denominator = every sideout reception found (regardless of
//     whether its channel/grade could be determined), matching DVMate's
//     `events.count`
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Hls from "hls.js";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/Language/LanguageContext";
import { useAuthStatus } from "../hooks/useAuthStatus";
import MultiChoice from "../layout/MultiChoice";
import { kSkillSet, kSkillSettersCall } from "../utils/Constants";
import { getDVRalliesInGameForTeam } from "../utils/StatsItem";
import { getTimingForEvent, makePlaylist, toHlsStreamUrl, zoneFromString } from "../utils/Utils";
import EventItem from "./EventItem";
import HittingChart from "./HittingChart";

const kGrades = ["-", "!", "+", "#"];
const kChannels = ["LEFT", "CENTRE", "RIGHT"];
const kChannelZones = [
  [4, 5, 7],
  [3, 6, 8],
  [1, 2, 9],
];
const kAllRows = ["1", "2", "3", "4", "5", "6"];

function emptyCell() {
  return {
    count: 0,
    kills: 0,
    errors: 0,
    attacks: 0,
    attackEvents: [],
    passEvents: [],
    passEventsWithAttack: [],
  };
}

function addCell(target, source) {
  target.count += source.count;
  target.kills += source.kills;
  target.errors += source.errors;
  target.attacks += source.attacks;
  target.attackEvents.push(...source.attackEvents);
  target.passEvents.push(...source.passEvents);
  target.passEventsWithAttack.push(...source.passEventsWithAttack);
}

function channelForZone(zone) {
  for (let i = 0; i < kChannelZones.length; i++) {
    if (kChannelZones[i].includes(zone)) return i;
  }
  return null;
}

// AdvanceCode4 is the raw scout-code zone digit (string position 10) — same
// as DVMate's Event+sup.m endZoneNumber. "~" or blank means uncoded.
function endZoneOf(ev) {
  const s = ev.AdvanceCode4;
  if (!s || s === "~") return 0;
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n;
}

function teamForMatch(match, team, selectedTeam, matchesLength) {
  if (matchesLength === 1) {
    return selectedTeam === 0 ? match.teamA : match.teamB;
  }
  return selectedTeam === 0
    ? team === match.teamA.Name
      ? match.teamA
      : match.teamB
    : team === match.teamA.Name
    ? match.teamB
    : match.teamA;
}

function getAttackComboOfEvent(match, code) {
  if (!code) return null;
  const acs = match.attackCombos?.filter((ac) => ac.code === code) ?? [];
  return acs.length > 0 ? acs[0] : null;
}

// Home/away by the same convention as EventsList.jsx's isHomeEvent.
function isHomeEvent(ev) {
  if (ev.dvString !== undefined) return ev.dvString[0] === "*";
  return ev.homePlayer === true;
}

function playerKey(pl) {
  if (!pl) return null;
  return pl.shirtNumber + ". " + pl.FirstName + " " + pl.LastName.toUpperCase();
}

// The rally's own set/setter's-call event (same side as the reception) —
// gives us both the setter (its Player) and the call code (its settersCall)
// in one lookup.
function setterEventForRally(mr) {
  if (!mr.passEvent || !mr.events) return null;
  const passIsHome = isHomeEvent(mr.passEvent);
  return (
    mr.events.find(
      (ev) =>
        (ev.EventType === kSkillSet || ev.EventType === kSkillSettersCall) && isHomeEvent(ev) === passIsHome
    ) ?? null
  );
}

function makeFilter(title, items) {
  return { title, items, singleSelection: false, allselected: true, selectedValues: items.map((i) => i.name) };
}

// Builds the 6 MultiChoice filter definitions (Setters/Passers/Calls/
// Rotations/Sets/Stages) from the full, UNFILTERED set of rallies, so the
// option lists themselves never shrink as the user toggles other filters.
function buildFilterDefs(matches, team, selectedGame, selectedTeam) {
  const setters = new Set();
  const passers = new Set();
  const calls = new Set();
  const sets = new Set();

  for (const match of matches) {
    const tm = teamForMatch(match, team, selectedTeam, matches.length);
    const games = !selectedGame || selectedGame === 0 ? match.sets : [match.sets[selectedGame - 1]];
    for (const game of games ?? []) {
      if (!game) continue;
      sets.add(game.GameNumber);
      const rallies = getDVRalliesInGameForTeam(game, tm);
      for (const mr of rallies) {
        if (!mr.passEvent || !mr.passEvent.Player) continue;
        const pk = playerKey(mr.passEvent.Player);
        if (pk) passers.add(pk);
        const setterEv = setterEventForRally(mr);
        const sk = playerKey(setterEv?.Player);
        if (sk) setters.add(sk);
        const call = setterEv?.settersCall ? setterEv.settersCall.substring(0, 2) : null;
        if (call) calls.add(call);
      }
    }
  }

  const toItems = (values) => [...values].sort().map((name) => ({ name, selected: true }));

  return {
    setters: makeFilter("Setters", toItems(setters)),
    passers: makeFilter("Passers", toItems(passers)),
    calls: makeFilter("Calls", toItems(calls)),
    rotations: makeFilter(
      "Rotations",
      ["1", "2", "3", "4", "5", "6"].map((name) => ({ name, selected: true }))
    ),
    sets: makeFilter(
      "Sets",
      [...sets].sort((a, b) => a - b).map((n) => ({ name: String(n), selected: true }))
    ),
  };
}

// Buckets attack events into HittingChart.jsx's own [row][zone] shape (6x9),
// using the exact same VBStats/DataVolley start-zone resolution as
// AttackZones.jsx/HittingChartReport.jsx's calculateZones(), so the court
// view here is identical to the one on the Hitting Charts report.
function bucketAttacksForHittingChart(attackEvents) {
  const events = Array.from({ length: 6 }, () => Array.from({ length: 9 }, () => []));
  for (const e of attackEvents) {
    const match = e.Drill?.match;
    if (!match || e.Row === undefined || isNaN(e.Row) || e.Row <= 0) continue;
    if (match.app === "VBStats") {
      const startZone = zoneFromString(e.BallStartString);
      if (startZone > 0) events[e.Row - 1][startZone - 1].push(e);
      continue;
    }
    const nacs = match.attackCombos?.length ?? 0;
    const ac = e.attackCombo === "~~" ? { code: "~~" } : getAttackComboOfEvent(match, e.attackCombo);
    if (ac === null || ac.code === undefined) continue;
    if (!((nacs > 0 && ac.targetHitter !== "-") || nacs === 0)) continue;
    let startZone = 0;
    if (nacs > 0) {
      if (ac.startZone !== undefined) {
        startZone = Number.parseInt(ac.startZone);
      } else if (ac.isBackcourt) {
        startZone = ac.targetHitter === "B" ? 9 : ac.targetHitter === "F" ? 7 : 8;
      } else {
        startZone = ac.targetHitter === "B" ? 2 : ac.targetHitter === "F" ? 4 : 3;
      }
    } else {
      startZone = zoneFromString(e.BallStartString);
    }
    if (startZone > 0) events[e.Row - 1][startZone - 1].push(e);
  }
  return events;
}

function calculateStats(matches, team, selectedGame, selectedTeam, filterDefs, selectedStages) {
  const grid = kGrades.map(() => kChannels.map(() => emptyCell()));
  let totalReceptions = 0;

  const selectedOf = (filter) => new Set(filter.items.filter((i) => i.selected).map((i) => i.name));
  const selectedSetters = selectedOf(filterDefs.setters);
  const selectedPassers = selectedOf(filterDefs.passers);
  const selectedCalls = selectedOf(filterDefs.calls);
  const selectedRotations = selectedOf(filterDefs.rotations);
  const selectedSets = selectedOf(filterDefs.sets);

  for (const match of matches) {
    const tm = teamForMatch(match, team, selectedTeam, matches.length);
    const games = !selectedGame || selectedGame === 0 ? match.sets : [match.sets[selectedGame - 1]];
    for (const game of games ?? []) {
      if (!game) continue;
      if (!selectedSets.has(String(game.GameNumber))) continue;
      const rallies = getDVRalliesInGameForTeam(game, tm);
      for (const mr of rallies) {
        if (!mr.passEvent || !mr.passEvent.Player) continue;
        if (!selectedRotations.has(String(mr.passEvent.Row))) continue;
        if (!selectedPassers.has(playerKey(mr.passEvent.Player))) continue;
        const setterEv = setterEventForRally(mr);
        const setterKey = playerKey(setterEv?.Player);
        if (setterKey != null && !selectedSetters.has(setterKey)) continue;
        const callKey = setterEv?.settersCall ? setterEv.settersCall.substring(0, 2) : null;
        if (callKey != null && !selectedCalls.has(callKey)) continue;

        totalReceptions++;

        // Side-out = the attack immediately following the reception;
        // transition = every later attack in the same rally (after a dig).
        // Only the stages currently selected contribute to this cell's
        // kills/errors/attacks (and the hitting chart below).
        const attacksToCount = [];
        if (selectedStages.has("SIDE OUT") && mr.spikeEvents?.[0]) attacksToCount.push(mr.spikeEvents[0]);
        if (selectedStages.has("TRANSITION")) attacksToCount.push(...(mr.spikeEvents ?? []).slice(1));

        const zone = endZoneOf(mr.passEvent);
        const channelIdx = zone > 0 ? channelForZone(zone) : null;
        const gradeIdx = kGrades.indexOf(mr.passEvent.DVGrade);
        if (channelIdx == null || gradeIdx === -1) continue;

        const cell = grid[gradeIdx][channelIdx];
        cell.count++;
        cell.passEvents.push(mr.passEvent);
        // Only the receptions that actually have a counted attack (per the
        // current Stages selection) feed the events-list panel — e.g. with
        // SIDE OUT only selected, a reception whose rally never reached an
        // attack (set error, etc.) has nothing to show on the hitting chart
        // either, so it's excluded here too, keeping the two counts in sync.
        if (attacksToCount.length > 0) cell.passEventsWithAttack.push(mr.passEvent);
        for (const atk of attacksToCount) {
          cell.attacks++;
          cell.attackEvents.push(atk);
          if (atk.EventGrade === 3) cell.kills++;
          else if (atk.EventGrade === 0) cell.errors++;
        }
      }
    }
  }

  return { grid, totalReceptions };
}

function Cell({ cell, totalReceptions, selected, onClick, readOnly, t }) {
  const freq = totalReceptions > 0 ? (cell.count * 100) / totalReceptions : 0;
  const eff = cell.attacks > 0 ? ((cell.kills - cell.errors) * 100) / cell.attacks : 0;
  return (
    <td
      onClick={onClick}
      className={
        "text-center px-2 py-1 text-xs whitespace-nowrap select-none border border-base-content/10" +
        (readOnly ? "" : " cursor-pointer hover:bg-base-300/40" + (selected ? "" : " opacity-30"))
      }
    >
      <div>
        {cell.count} {t("passes")} ({freq.toFixed(0)}%)
      </div>
      <div className="opacity-70">
        {cell.kills}K | {cell.errors}E ({eff.toFixed(0)}% {t("eff.")})
      </div>
    </td>
  );
}

function ServeReceiveByZones({ matches, team, selectedGame, selectedTeam }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuthStatus();

  // Per-cell toggle state: the 4x3 grade/channel grid — mirrors DVMate's
  // SetterAnalysisStats.selected, toggled independently per cell. All start
  // selected.
  const [selectedGrid, setSelectedGrid] = useState(() => kGrades.map(() => kChannels.map(() => true)));

  // The 5 MultiChoice filter definitions (Setters/Passers/Calls/Rotations/
  // Sets). MultiChoice mutates `filter.items[i].selected` in place, so
  // these objects must keep their identity across re-renders — only rebuilt
  // (resetting every filter to "select all") when the underlying match data
  // actually changes. `filterVersion` is bumped on every toggle purely to
  // retrigger the calculateStats useMemo below (the mutation itself isn't a
  // dependency React can see).
  const filterDefs = useMemo(
    () => buildFilterDefs(matches ?? [], team, selectedGame, selectedTeam),
    [matches, team, selectedGame, selectedTeam]
  );
  const [filterVersion, setFilterVersion] = useState(0);
  const onFilterOptionChanged = () => setFilterVersion((v) => v + 1);

  // Stages (SIDE OUT / TRANSITION) checkboxes above the table, next to
  // Reset — not a MultiChoice, just two plain toggles. Default both on.
  const [sideOutSelected, setSideOutSelected] = useState(true);
  const [transitionSelected, setTransitionSelected] = useState(true);
  const selectedStages = useMemo(() => {
    const s = new Set();
    if (sideOutSelected) s.add("SIDE OUT");
    if (transitionSelected) s.add("TRANSITION");
    return s;
  }, [sideOutSelected, transitionSelected]);

  const { grid, totalReceptions } = useMemo(
    () => calculateStats(matches ?? [], team, selectedGame, selectedTeam, filterDefs, selectedStages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matches, team, selectedGame, selectedTeam, filterDefs, filterVersion, selectedStages]
  );

  // Read-only "SELECTED" row: per-channel sum of whichever grade cells are
  // currently toggled on — not an independently selectable cell itself, and
  // not included in the hitting chart below.
  const selectedTotals = useMemo(
    () =>
      kChannels.map((_, ci) => {
        const total = emptyCell();
        kGrades.forEach((_, gi) => {
          if (selectedGrid[gi][ci]) addCell(total, grid[gi][ci]);
        });
        return total;
      }),
    [grid, selectedGrid]
  );

  // The hitting chart shows the union of every selected grid cell's attacks
  // only — the derived "SELECTED" row is a read-only summary and never
  // contributes its own data here (it would just double up what's already
  // counted via the grid cells it sums).
  const chartEvents = useMemo(() => {
    const seen = new Set();
    const union = [];
    kGrades.forEach((_, gi) => {
      kChannels.forEach((_, ci) => {
        if (!selectedGrid[gi][ci]) return;
        for (const e of grid[gi][ci].attackEvents) {
          if (!seen.has(e)) {
            seen.add(e);
            union.push(e);
          }
        }
      });
    });
    return bucketAttacksForHittingChart(union);
  }, [grid, selectedGrid]);

  // The events list (right sub-panel) shows the serve-receive events
  // themselves (passEvent), not the attacks — union of every selected grid
  // cell's passEventsWithAttack (receptions that have a counted attack for
  // the current Stages selection, same ones feeding the chart above), so
  // the events-list count and the hitting chart's attack count stay in
  // sync — e.g. with SIDE OUT only selected, each listed reception has
  // exactly one corresponding attack on the chart.
  const selectedSREvents = useMemo(() => {
    const seen = new Set();
    const list = [];
    kGrades.forEach((_, gi) => {
      kChannels.forEach((_, ci) => {
        if (!selectedGrid[gi][ci]) return;
        for (const e of grid[gi][ci].passEventsWithAttack) {
          if (!seen.has(e)) {
            seen.add(e);
            list.push(e);
          }
        }
      });
    });
    list.sort((a, b) => (a.TimeStamp?.getTime() ?? 0) - (b.TimeStamp?.getTime() ?? 0));
    return list;
  }, [grid, selectedGrid]);

  // The events list (right sub-panel) is grouped by set/game for a single
  // match, but by MATCH for a multi-match report (set numbers aren't a
  // meaningful grouping across different matches). The label shows only the
  // OPPOSITION name (not "teamA vs teamB") since "our" team is the same
  // across every match in the report.
  const srEventGroups = useMemo(() => {
    const byMatch = (matches ?? []).length > 1;
    const groups = new Map();
    for (const ev of selectedSREvents) {
      const match = ev.Drill?.match;
      const gameNumber = ev.Drill?.GameNumber ?? 0;
      const matchKey = match?.guid ?? match?.dvstring ?? "";
      const key = byMatch ? matchKey : matchKey + "-" + gameNumber;
      if (!groups.has(key)) {
        let label = null;
        if (byMatch && match) {
          const ourTeam = teamForMatch(match, team, selectedTeam, matches.length);
          const opposition = ourTeam === match.teamA ? match.teamB : match.teamA;
          label = opposition?.Name ?? "?";
        }
        groups.set(key, {
          label,
          gameNumber,
          sortKey: byMatch ? (matches?.indexOf(match) ?? 0) : gameNumber,
          events: [],
        });
      }
      groups.get(key).events.push(ev);
    }
    return [...groups.values()].sort((a, b) => a.sortKey - b.sortKey);
  }, [selectedSREvents, matches, team, selectedTeam]);

  // Video player wiring for the events list — mirrors MomentumChartPage.jsx's
  // simpler single-report pattern (YouTube / HLS / plain file via
  // ReactPlayer), extended to switch the active match (and its video source)
  // when a clicked event belongs to a different match, as VideoAnalysis.jsx's
  // doSelectEvent does for multi-match reports.
  const playerRef = useRef();
  const hlsVideoRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const currentMatchRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [isYoutube, setIsYoutube] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Attaches hls.js to the raw <video> for .m3u8 sources — without this the
  // element has no src/MediaSource at all and onCanPlay never fires.
  useEffect(() => {
    if (!videoFilePath?.endsWith(".m3u8")) return;
    const vid = hlsVideoRef.current;
    if (!vid) return;
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    if (!Hls.isSupported()) {
      vid.src = videoFilePath;
    } else {
      const hls = new Hls({ xhrSetup: (xhr) => { xhr.withCredentials = false; } });
      hls.loadSource(videoFilePath);
      hls.attachMedia(vid);
      hlsInstanceRef.current = hls;
    }
    return () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  }, [videoFilePath]);

  // Expands a youtu.be short link to the full watch URL (same regex as
  // VideoAnalysis.jsx's local convertYouTubeUrl).
  const expandYouTubeUrl = (str) => {
    if (!str || !str.includes("youtu.be")) return { url: str, isYoutube: false };
    const m = /.*\/([0-9a-zA-Z-_]*)/.exec(str);
    if (m?.[1]) return { url: "https://www.youtube.com/watch?v=" + m[1], isYoutube: true };
    return { url: str, isYoutube: false };
  };

  const loadMatchVideo = (match) => {
    let onlineUrl = match.videoOnlineUrl;
    if (!onlineUrl) {
      const prefix = match.app === "VBStats" ? match.Guid : match.dvstring;
      try {
        const vobj = JSON.parse(localStorage.getItem(prefix + "_videoInfo"));
        if (vobj?.matchDVString === match.dvstring) onlineUrl = vobj.videoOnlineUrl;
      } catch {
        /* no cached video info for this match */
      }
    }
    if (!onlineUrl) {
      setVideoFilePath(null);
      return;
    }
    const { url, isYoutube: yt } = expandYouTubeUrl(onlineUrl);
    setIsYoutube(yt);
    setVideoFilePath(toHlsStreamUrl(url));
  };

  // Loads the first match's video by default so the player isn't empty
  // before any serve-receive event has been clicked.
  useEffect(() => {
    const first = matches?.[0];
    if (!first) return;
    currentMatchRef.current = first;
    setIsReady(false);
    loadMatchVideo(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  const performSeek = (pos) => {
    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        vid.play().catch(() => {});
      }
      return;
    }
    if (isYoutube) {
      const yt = playerRef.current?.getInternalPlayer();
      yt?.seekTo?.(pos, true);
      yt?.playVideo?.();
      return;
    }
    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
      vid.play().catch(() => {});
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  const seekToAndPlay = (seconds) => {
    if (seconds == null || !isFinite(seconds)) return;
    const pos = Math.max(0, seconds);
    if (!isReady) {
      pendingSeekRef.current = pos;
      return;
    }
    performSeek(pos);
  };

  const handlePlayerReady = () => {
    setIsReady(true);
    if (pendingSeekRef.current != null) {
      const pos = pendingSeekRef.current;
      pendingSeekRef.current = null;
      performSeek(pos);
    }
  };

  // Seeks the video to the clicked serve-receive event, switching the active
  // video source first if it belongs to a different match (multi-match
  // reports) — mirrors VideoAnalysis.jsx's doSelectEvent.
  const doSelectSREvent = (ev) => {
    setSelectedEvent(ev);
    const match = ev.Drill?.match;
    if (!match) return;
    const tm = getTimingForEvent(ev);
    let vt = null;
    if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
      vt = ev.VideoPosition - tm.leadTime;
    } else if (match.videoStartTimeSeconds != null && ev.TimeStamp) {
      const secondsSinceEpoch = Math.round(ev.TimeStamp.getTime() / 1000);
      const offset = match.videoOffset != null && match.videoOffset >= 0 ? match.videoOffset : 0;
      vt = secondsSinceEpoch - match.videoStartTimeSeconds + offset - tm.leadTime;
    }
    if (match !== currentMatchRef.current) {
      currentMatchRef.current = match;
      setIsReady(false);
      loadMatchVideo(match);
      pendingSeekRef.current = vt;
    } else {
      seekToAndPlay(vt);
    }
  };

  const toggleCell = (gi, ci) => {
    setSelectedGrid((prev) => prev.map((row, ri) => (ri === gi ? row.map((v, cii) => (cii === ci ? !v : v)) : row)));
  };
  // Clicking a grade label flips every channel cell in that row individually
  // (not a synchronized select-all) — matches DVMate's togglePassingSelection
  // grade-button handling.
  const toggleGradeRow = (gi) => {
    setSelectedGrid((prev) => prev.map((row, ri) => (ri === gi ? row.map((v) => !v) : row)));
  };
  // Clicking a channel label flips every grade cell in that column — matches
  // DVMate's side-button handling.
  const toggleChannelColumn = (ci) => {
    setSelectedGrid((prev) => prev.map((row) => row.map((v, cii) => (cii === ci ? !v : v))));
  };
  const resetSelection = () => {
    setSelectedGrid(kGrades.map(() => kChannels.map(() => true)));
  };

  const doEventsSelected = (evs) => {
    const sorted = [...evs].sort((a, b) => a.TimeStamp.getTime() - b.TimeStamp.getTime());
    const playlist = makePlaylist(sorted);
    if (playlist.length === 0) {
      toast.error(t("No events in selected zone."));
      return;
    }
    localStorage.setItem("VBLivePlaylistEvents", JSON.stringify(playlist));
    const st = {
      playlistFileData: JSON.stringify({ events: playlist }),
      filename: null,
      playlists: null,
      serverName: currentUser.email,
    };
    navigate("/playlist", { state: st });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 p-4 h-[calc(100vh-6rem)]">
      <div className="flex-1 flex flex-col gap-4 min-h-0">
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4">
          {/* Left sub-panel: hitting chart */}
          <div className="flex-1 min-h-0 flex justify-center overflow-auto">
            <HittingChart
              matches={matches}
              events={chartEvents}
              rows={kAllRows}
              drawMode={0}
              onEventsSelected={doEventsSelected}
            />
          </div>

          {/* Right sub-panel: serve-receive events currently selected (see
              selectedSREvents), grouped by set (single match) or by match
              (multi-match report) — same per-event row as Video Analysis's
              EventsList (EventItem), click to seek */}
          <div className="w-full lg:w-80 shrink-0 min-h-0 overflow-y-auto bg-base-300/30 rounded-md">
            {srEventGroups.length === 0 ? (
              <div className="p-4 text-sm text-base-content/50">{t("No events in selection.")}</div>
            ) : (
              srEventGroups.map((g, gi) => (
                <details key={gi} open className="group/set">
                  <summary className="flex items-center gap-2 px-3 py-2 bg-primary/10 border-l-4 border-l-primary cursor-pointer select-none hover:bg-primary/15 list-none [&::-webkit-details-marker]:hidden [&::marker]:hidden [&::before]:hidden">
                    <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-primary transition-transform duration-150 group-open/set:rotate-90" />
                    <span className="flex-1 text-sm font-bold tracking-wide">
                      {g.label ?? `${t("Game").toUpperCase()} ${g.gameNumber}`}
                    </span>
                    <span className="text-xs opacity-60">{g.events.length}</span>
                  </summary>
                  <div className="ml-5 border-l border-l-primary/20 divide-y divide-base-200/60">
                    {g.events.map((ev, ei) => (
                      <EventItem
                        key={ei}
                        event={ev}
                        isFirst={ei === 0}
                        isSelected={ev === selectedEvent}
                        onEventSelected={doSelectSREvent}
                      />
                    ))}
                  </div>
                </details>
              ))
            )}
          </div>
        </div>

        <div className="overflow-x-auto shrink-0">
          <div className="flex items-center justify-end gap-3 mb-1">
            <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={sideOutSelected}
                onChange={() => setSideOutSelected((v) => !v)}
              />
              {t("SIDE OUT")}
            </label>
            <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={transitionSelected}
                onChange={() => setTransitionSelected((v) => !v)}
              />
              {t("TRANSITION")}
            </label>
            <button type="button" className="btn btn-xs" onClick={resetSelection}>
              {t("Reset")}
            </button>
          </div>
          <table className="table-generic border-collapse">
            <thead className="thead-generic">
              <tr>
                <th className="text-left px-2 py-1 text-xs border border-base-content/10">{t("Grade")}</th>
                {kChannels.map((ch, ci) => (
                  <th
                    key={ch}
                    className="text-center px-2 py-1 text-xs cursor-pointer select-none hover:underline border border-base-content/10"
                    onClick={() => toggleChannelColumn(ci)}
                  >
                    {t(ch)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kGrades.map((grade, gi) => (
                <tr key={grade} className={gi % 2 === 0 ? "bg-transparent" : "bg-base-300/10"}>
                  <td
                    className="px-2 py-1 text-sm font-bold text-center cursor-pointer select-none hover:underline border border-base-content/10"
                    onClick={() => toggleGradeRow(gi)}
                  >
                    {grade}
                  </td>
                  {kChannels.map((_, ci) => (
                    <Cell
                      key={ci}
                      cell={grid[gi][ci]}
                      totalReceptions={totalReceptions}
                      selected={selectedGrid[gi][ci]}
                      onClick={() => toggleCell(gi, ci)}
                      t={t}
                    />
                  ))}
                </tr>
              ))}
              <tr className="bg-base-300/30">
                <td className="px-2 py-1 text-sm font-bold text-center border border-base-content/10">{t("SELECTED")}</td>
                {kChannels.map((_, ci) => (
                  <Cell
                    key={ci}
                    cell={selectedTotals[ci]}
                    totalReceptions={totalReceptions}
                    readOnly
                    t={t}
                  />
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-4 min-h-0">
        {/* Column 2, top row: video of the currently selected SR event */}
        <div className="shrink-0 aspect-video bg-black rounded-md overflow-hidden">
          {videoFilePath ? (
            videoFilePath.endsWith(".m3u8") ? (
              <video
                ref={hlsVideoRef}
                controls
                onCanPlay={handlePlayerReady}
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            ) : (
              <ReactPlayer
                ref={playerRef}
                url={videoFilePath}
                playing
                width="100%"
                height="100%"
                controls
                onReady={handlePlayerReady}
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full text-base-content/50 text-sm">
              {t("No video available for this match.")}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto border border-base-300 rounded-md p-1 flex flex-col gap-1">
          {[
            [filterDefs.setters, filterDefs.passers],
            [filterDefs.calls, filterDefs.rotations, filterDefs.sets],
          ].map((row, ri) => (
            <div key={ri} className="flex gap-1">
              {row.map((filter) => (
                <div
                  key={filter.title}
                  tabIndex={0}
                  className="collapse collapse-arrow rounded-sm flex-1 min-w-0 border border-base-300 shadow-sm"
                >
                  <input type="checkbox" className="peer" defaultChecked />
                  <div className="collapse-title min-h-0 py-1 bg-base-200 text-xs font-medium peer-checked:bg-base-100 peer-checked:text-base-700">
                    {t(filter.title).toUpperCase()}
                  </div>
                  <div className="collapse-content !min-h-0 !pt-1 max-h-36 overflow-y-auto peer-checked:bg-base-100">
                    <MultiChoice filter={filter} handleOptionChanged={onFilterOptionChanged} compact />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ServeReceiveByZones;
